import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { FaTrashAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import "../assets/styles/CartPopup.css";

const CartPopup = () => {
  const {
    cartItems,
    isCartOpen,
    toggleCart,
    removeFromCart,
    updateQuantity,
    selectedItems,
    setSelectedItems,

  } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const [itemToRemove, setItemToRemove] = useState(null);
  const navigate = useNavigate();

  const subtotal = cartItems.reduce((total, item) => {
    if (!selectedItems[item.id] || !item.price) return total;

    // Extract numeric value from price string (e.g., "$44.00" -> 44.00)
    const priceValue = parseFloat(item.price.replace(/[^0-9.]/g, "")) || 0;

    return total + priceValue * (item.quantity || 1);
  }, 0);

  const handleImageClick = () => {
    toggleCart();
  };

  const handleRemoveClick = (item) => {
    setItemToRemove(item);
  };

  const handleConfirmRemove = () => {
    if (itemToRemove) {
      removeFromCart(itemToRemove.id);
      setItemToRemove(null);
    }
  };

  const handleMoveToWishlist = () => {
    if (itemToRemove) {
      addToWishlist(itemToRemove);
      removeFromCart(itemToRemove.id);
      setItemToRemove(null);
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not its children
    if (e.target.className === "remove-confirmation-overlay") {
      setItemToRemove(null);
    }
  };

  const handleCheckboxChange = (itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const hasSelectedItems = () => {
    return Object.values(selectedItems).some((value) => value === true);
  };

  const handleProceedToBuy = () => {
    if (hasSelectedItems()) {
      // Save selected items to localStorage
      localStorage.setItem("selectedItems", JSON.stringify(selectedItems));
      // Close the cart popup
      toggleCart();
      // Set a flag in sessionStorage to indicate intentional navigation
      sessionStorage.setItem("intentionalNavigation", "true");
      // Navigate to address page
      navigate("/address");
    }
  };

  const handleQuantityUpdate = (itemId, newQuantity, currentStock) => {
    if (newQuantity > currentStock) {
      // Show error message or alert
      alert(`Sorry, only ${currentStock} items available in stock`);
      return;
    }
    updateQuantity(itemId, newQuantity);
  };

  if (!isCartOpen) return null;

  return (
    <>
      <div className="cart-popup-overlay">
        <div className="cart-popup">
          <div className="cart-header">
            <h2>Cart</h2>
            <button className="close-btn" onClick={toggleCart}>
              ×
            </button>
          </div>

          {cartItems.length > 0 ? (
            <>
              <div className="cart-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-item">
                    <input
                      type="checkbox"
                      className="cart-item-checkbox"
                      checked={selectedItems[item.id] || false}
                      onChange={() => handleCheckboxChange(item.id)}
                    />
                    <Link
                      to={`/product/${item.id}`}
                      className="item-image"
                      onClick={handleImageClick}
                    >
                      <img src={item.images[0]} alt={item.name} />
                    </Link>
                    <div className="item-details">
                      <h3>{item.name}</h3>
                      <p>Color: {item.color}</p>
                      <div className="quantity-controls">
                        <button
                          onClick={() =>
                            handleQuantityUpdate(
                              item.id,
                              Math.max(1, item.quantity - 1),
                              item.stock
                            )
                          }
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            handleQuantityUpdate(
                              item.id,
                              item.quantity + 1,
                              item.stock
                            )
                          }
                          disabled={item.quantity >= item.stock}
                        >
                          +
                        </button>
                      </div>
                      <p className="price">{item.price}</p>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveClick(item)}
                      title="Remove item"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                ))}
              </div>
              <div className="cart-footer">
                <div className="subtotal">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <p className="checkout-note">
                  Shipping, taxes, and discount codes calculated at checkout.
                </p>
                <button
                  className="proceed-to-buy"
                  disabled={!hasSelectedItems()}
                  onClick={handleProceedToBuy}
                >
                  PROCEED TO CHECKOUT
                </button>
              </div>
            </>
          ) : (
            <div className="empty-cart">
              <p>Your cart is empty</p>
            </div>
          )}
        </div>
      </div>

      {itemToRemove && (
        <div
          className="remove-confirmation-overlay"
          onClick={handleOverlayClick}
        >
          <div className="remove-confirmation">
            <button className="close-btn" onClick={() => setItemToRemove(null)}>
              ×
            </button>
            <div className="remove-confirmation-content">
              <div className="remove-confirmation-image">
                <img src={itemToRemove.images[0]} alt={itemToRemove.name} />
              </div>
              <div className="remove-confirmation-text">
                <h3>Move from Cart</h3>
                <p>Are you sure you want to remove this item from your cart?</p>
              </div>
            </div>
            <div className="remove-confirmation-buttons">
              <button
                className="remove-btn-confirm"
                onClick={handleConfirmRemove}
              >
                REMOVE
              </button>
              <button
                className="move-to-wishlist-btn"
                onClick={handleMoveToWishlist}
                disabled={isInWishlist(itemToRemove.id)}
              >
                {isInWishlist(itemToRemove.id)
                  ? "ALREADY IN WISHLIST"
                  : "MOVE TO WISHLIST"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CartPopup;
